import {
  GET_SCHEDULES_SUCCESS,
  ADD_SCHEDULE_RULE,
  CHANGE_CATEGORIES,
  SAVE_SCHEDULES_SUCCESS,
  DISCARD_SCHEDULE_CHANGES,
  EDIT_SCHEDULE_RULE,
  DELETE_SCHEDULE_RULE,
  TOGGLE_SCHEDULE_ENABLED,
} from "./schedules.types";
import { Schedule } from "@sportal/api/schedules";
import { getSubscriberId } from "../info";
import { SCHEDULE_TYPES } from "../../pages/profile/scheduler/scheduler.consts";
import { getProfile } from "../profiles";
import { isFailure, Profile } from "@sportal/api";
import {
  dehydrateSchedule,
  getBlockedCategories,
  getProfileSchedulesByType,
  getRemovedSchedules,
  getScheduleStatusForSave,
  hasCategoriesChanges,
} from "./schedules.selectors";
import { createTempNotification } from "../../components/notifications";
import _ from "lodash";

export const getSchedulesSuccess = (payload: Profile[]) => ({
  type: GET_SCHEDULES_SUCCESS,
  payload,
});

export const addScheduleRule = (scheduleRule: Schedule) => ({
  type: ADD_SCHEDULE_RULE,
  payload: scheduleRule,
});

export const changeScheduleRule = (scheduleRule: Schedule) => ({
  type: EDIT_SCHEDULE_RULE,
  payload: scheduleRule,
});

export const toggleScheduleRules = (
  profileId: string,
  type: string,
  enabled: boolean,
) => ({
  type: TOGGLE_SCHEDULE_ENABLED,
  payload: { profileId, type, enabled },
});

export const removeScheduleRule = (scheduleRule: Schedule) => ({
  type: DELETE_SCHEDULE_RULE,
  payload: scheduleRule,
});

export const changeCategories = (categories, profileId) => ({
  type: CHANGE_CATEGORIES,
  payload: { categories, profileId },
});

export const homeworkSchedule = (type) => type === SCHEDULE_TYPES.homework;

export const saveScheduleRules =
  (profileId: string, type: string) =>
  async (dispatch, getState, { api }) => {
    const state = getState();
    const subscriberId = getSubscriberId(state);
    const profileName = getProfile(state, profileId).name;
    const categories = homeworkSchedule(type)
      ? getBlockedCategories(profileId)(state)
      : [];
    const hasCategoryChanges = homeworkSchedule(type)
      ? hasCategoriesChanges(profileId)(state)
      : false;
    const allScheduleRules = getProfileSchedulesByType(profileId, type)(state);

    const added = [];
    const changed = [];
    const removed = getRemovedSchedules(profileId, type)(state);

    allScheduleRules.forEach((schedule) => {
      const { isNew, isChanged } = getScheduleStatusForSave(state, schedule.id);
      const normalizedSchedule = dehydrateSchedule({ ...schedule, categories });

      if (isNew) {
        added.push(normalizedSchedule);
      } else if (isChanged || hasCategoryChanges) {
        changed.push(normalizedSchedule);
      }
    });

    if (added.length === 0 && changed.length === 0 && removed.length === 0) {
      return;
    }

    return buildChain(removed, (schedule) =>
      api.schedules.delete(subscriberId, profileName, schedule.name),
    )
      .then(() =>
        buildChain(added, (schedule) =>
          api.schedules.create(subscriberId, profileName, schedule),
        ),
      )
      .then(() =>
        buildChain(changed, (schedule) =>
          api.schedules.update(
            subscriberId,
            profileName,
            schedule.name,
            schedule,
          ),
        ),
      )
      .then(() => {
        dispatch(
          createTempNotification({
            variant: "success",
            description: `${type}_schedule_save_success`,
          }),
        );
        dispatch(
          saveScheduleSuccess({ added, changed, removed, type, profileId }),
        );
      })
      .catch(() => {
        dispatch(
          createTempNotification({
            variant: "error",
            description: `${type}_schedule_save_failure`,
          }),
        );
      });
  };

const buildChain = (schedules, apiCall) => {
  return _.reduce(
    schedules,
    (chain, schedule) => {
      return chain.then(async () => {
        const result = await apiCall(schedule);

        if (isFailure(result)) {
          return Promise.reject(result);
        }
      });
    },
    Promise.resolve(),
  );
};

export const saveScheduleSuccess = (schedules) => ({
  type: SAVE_SCHEDULES_SUCCESS,
  payload: schedules,
});

export const discardScheduleChanges = (profileId: string, type: string) => ({
  type: DISCARD_SCHEDULE_CHANGES,
  payload: { profileId, type },
});
